// اجزای SCR-STAFF-202 (AP-CAT-02): دیالوگ فهرست قیمت شعبه (US-CAT-023)، تب گروه‌های ارائه‌دهنده (AC-CAT-022.1؛ هر ارائه‌دهنده حداکثر
// یک گروه) و تب پروفایل‌های مالیاتی پویا با نرخ‌های تاریخ‌دار (US-CAT-004، AC-CAT-004.3؛ مدیریت با cat.tax.manage).
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type CatalogTaxCategory,
  type ProviderGroupView,
  type TaxProfileView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  SelectRHF,
  TextFieldRHF,
  useDisplaySettings,
  zBusinessDate,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  catKeys,
  fromBasisPoints,
  todayIn,
  toBasisPoints,
  useBranches,
  useProviderGroups,
  useProviders,
  useTaxProfiles,
} from './common';

const CODE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,31}$/;

const branchSchema = z.object({
  branchId: z.string().min(1, { error: 'common:validation.required' }),
  name: zRequiredText(200),
});
type BranchForm = z.input<typeof branchSchema>;

/** دیالوگ فهرست قیمت شعبه تازه؛ پس از ایجاد شناسه فهرست برمی‌گردد. */
export function BranchListDialog({ onClose }: { onClose: (createdId?: string) => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const branches = useBranches();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<BranchForm>({
    resolver: zodResolver(branchSchema),
    defaultValues: { branchId: '', name: '' },
  });

  const save = useMutation({
    mutationFn: (form: BranchForm) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/price-lists', {
          body: { branchId: form.branchId, name: form.name },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: catKeys.priceLists(session?.tenantId) });
      onClose(created.id);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['branchId', 'name'],
        codeFields: { 'PHX-CAT-0706': 'branchId', 'PHX-CAT-0707': 'branchId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-202-BRANCH-LIST"
      titleKey="cat:prices.add_branch_list"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <SelectRHF
          control={control}
          name="branchId"
          labelKey="cat:prices.branch"
          required
          fullWidth
          options={branches.branches.map((branch) => ({ value: branch.id, label: branch.name }))}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="cat:prices.list_name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'branch-list-name' } }}
        />
      </Stack>
    </FormDialog>
  );
}

const groupSchema = z.object({
  code: z.string().regex(CODE, { error: 'errors:validation.invalid' }),
  name: zRequiredText(200),
  providerIds: z.array(z.string()),
});
type GroupForm = z.input<typeof groupSchema>;

function GroupDialog({ group, onClose }: { group: ProviderGroupView | null; onClose: () => void }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const providers = useProviders();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<GroupForm>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      code: group?.code ?? '',
      name: group?.name ?? '',
      providerIds: [...(group?.providerIds ?? [])],
    },
  });

  const save = useMutation({
    mutationFn: (form: GroupForm) =>
      unwrap(
        group
          ? tenantApi.PUT('/api/v1/catalog/provider-groups/{id}', {
              params: { path: { id: group.id } },
              body: { ...form, rowVersion: group.rowVersion },
            })
          : tenantApi.POST('/api/v1/catalog/provider-groups', {
              body: form,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'providerIds'],
        codeFields: { 'PHX-CAT-0708': 'code', 'PHX-CAT-0709': 'providerIds', 'PHX-CAT-0302': 'providerIds' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-202-GROUP"
      titleKey={group ? 'cat:groups.edit_title' : 'cat:groups.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextFieldRHF
            control={control}
            name="code"
            labelKey="cat:groups.code"
            required
            fullWidth
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'group-code' } }}
          />
          <TextFieldRHF
            control={control}
            name="name"
            labelKey="cat:groups.name"
            required
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'group-name' } }}
          />
        </Stack>
        <Controller
          control={control}
          name="providerIds"
          render={({ field, fieldState }) => (
            <Autocomplete
              multiple
              options={providers.options.map((option) => option.id)}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              getOptionLabel={(id) => providers.name(id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('cat:groups.members')}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ? t(fieldState.error.message) : t('cat:groups.members_help')
                  }
                />
              )}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}

/** تب گروه‌های ارائه‌دهنده. */
export function ProviderGroupsTab() {
  const { t } = useTranslation('cat');
  const write = useWriteAccess('cat.price.manage');
  const groups = useProviderGroups();
  const providers = useProviders();
  const [editing, setEditing] = useState<ProviderGroupView | null | undefined>(undefined);

  const columns: DataTableColumn<ProviderGroupView>[] = [
    { id: 'code', headerKey: 'cat:groups.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'name', headerKey: 'cat:groups.name', cell: (row) => row.name },
    {
      id: 'members',
      headerKey: 'cat:groups.members',
      cell: (row) => row.providerIds.map((id) => providers.name(id)).join(t('cat:list_separator')) || t('common:states.none'),
    },
    {
      id: 'actions',
      headerKey: 'cat:prices.columns.actions',
      cell: (row) => (
        <IconButton
          aria-label={t('cat:groups.edit_named', { name: row.name })}
          disabled={!write.canWrite}
          onClick={() => setEditing(row)}
        >
          <EditOutlinedIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Typography variant="body2" color="text.secondary">
        {t('cat:groups.help')}
      </Typography>
      <DataTable
        captionKey="cat:groups.caption"
        columns={columns}
        rows={groups.data?.items}
        getRowId={(row) => row.id}
        isLoading={groups.isPending}
        error={groups.error}
        onRetry={() => void groups.refetch()}
        emptyTitleKey="cat:groups.empty"
        toolbar={
          <Stack direction="row" sx={{ p: 1.5 }}>
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() => setEditing(null)}
              data-testid="group-create"
            >
              {t('cat:groups.add')}
            </Button>
          </Stack>
        }
      />
      {editing !== undefined && <GroupDialog group={editing} onClose={() => setEditing(undefined)} />}
    </Stack>
  );
}

const profileSchema = z
  .object({
    code: z.string().regex(CODE, { error: 'errors:validation.invalid' }),
    name: zRequiredText(200),
    category: z.enum(['Exempt', 'Taxable', 'TaxableCustom']),
    moadianCode: z
      .string()
      .trim()
      .refine((value) => value === '' || /^[0-9]{13}$/.test(value), {
        message: 'errors:validation.moadian_code',
      }),
    exemptionRule: z.string().max(500, { error: 'errors:validation.max_length' }),
    isDefault: z.boolean(),
    isActive: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.category === 'Exempt' && !value.exemptionRule.trim()) {
      ctx.addIssue({ code: 'custom', path: ['exemptionRule'], message: 'common:validation.required' });
    }
  });
type ProfileForm = z.input<typeof profileSchema>;

function ProfileDialog({ profile, onClose }: { profile: TaxProfileView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      code: profile?.code ?? '',
      name: profile?.name ?? '',
      category: profile?.category ?? 'Taxable',
      moadianCode: profile?.moadianCode ?? '',
      exemptionRule: profile?.exemptionRule ?? '',
      isDefault: profile?.isDefault ?? false,
      isActive: profile?.isActive ?? true,
    },
  });
  const category = useWatch({ control, name: 'category' });

  const save = useMutation({
    mutationFn: (form: ProfileForm) => {
      const body = {
        code: form.code,
        name: form.name,
        category: form.category as CatalogTaxCategory,
        moadianCode: form.moadianCode.trim() || null,
        exemptionRule: form.category === 'Exempt' ? form.exemptionRule.trim() : null,
        isDefault: form.isDefault,
        isActive: form.isActive,
      };
      return unwrap(
        profile
          ? tenantApi.PUT('/api/v1/catalog/tax-profiles/{id}', {
              params: { path: { id: profile.id } },
              body: { ...body, rowVersion: profile.rowVersion },
            })
          : tenantApi.POST('/api/v1/catalog/tax-profiles', {
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'moadianCode', 'exemptionRule'],
        codeFields: { 'PHX-CAT-0501': 'code' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-202-TAX-PROFILE"
      titleKey={profile ? 'cat:tax_profiles.edit_title' : 'cat:tax_profiles.create_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextFieldRHF
            control={control}
            name="code"
            labelKey="cat:tax_profiles.code"
            required
            fullWidth
            disabled={Boolean(profile)}
            slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tax-profile-code' } }}
          />
          <TextFieldRHF
            control={control}
            name="name"
            labelKey="cat:tax_profiles.name"
            required
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'tax-profile-name' } }}
          />
        </Stack>
        <SelectRHF
          control={control}
          name="category"
          labelKey="cat:tax_profiles.category"
          disabled={Boolean(profile)}
          options={(['Exempt', 'Taxable', 'TaxableCustom'] as const).map((value) => ({
            value,
            labelKey: `cat:tax_category.${value}`,
          }))}
        />
        {category === 'Exempt' && (
          <TextFieldRHF
            control={control}
            name="exemptionRule"
            labelKey="cat:tax_profiles.exemption_rule"
            helperKey="cat:tax_profiles.exemption_rule_help"
            required
            fullWidth
            slotProps={{ htmlInput: { 'data-testid': 'tax-profile-rule' } }}
          />
        )}
        <TextFieldRHF
          control={control}
          name="moadianCode"
          labelKey="cat:tax.moadian_code"
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
        />
        <CheckboxRHF
          control={control}
          name="isDefault"
          labelKey="cat:tax_profiles.is_default"
          helperKey="cat:tax_profiles.is_default_help"
        />
        <CheckboxRHF control={control} name="isActive" labelKey="cat:tax_profiles.is_active" />
      </Stack>
    </FormDialog>
  );
}

function rateSchema(today: string) {
  return z
    .object({
      effectiveFrom: zBusinessDate(),
      components: z
        .array(
          z.object({
            code: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/, { error: 'errors:validation.invalid' }),
            percent: z
              .string()
              .refine((value) => value.trim() !== '' && Number(value) >= 0 && Number(value) <= 100, {
                message: 'errors:validation.invalid',
              }),
          }),
        )
        .min(1, { error: 'common:validation.required' }),
    })
    .superRefine((value, ctx) => {
      if (value.effectiveFrom && value.effectiveFrom < today) {
        ctx.addIssue({
          code: 'custom',
          path: ['effectiveFrom'],
          message: 'errors:error.price_effective_date_past',
        });
      }
    });
}
type RateForm = z.input<ReturnType<typeof rateSchema>>;

function RateDialog({ profile, onClose }: { profile: TaxProfileView; onClose: () => void }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const today = todayIn(timeZone);
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<RateForm>({
    resolver: zodResolver(rateSchema(today)),
    defaultValues: { effectiveFrom: today, components: [{ code: 'vat', percent: '' }] },
  });
  const components = useFieldArray({ control, name: 'components' });

  const save = useMutation({
    mutationFn: (form: RateForm) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/tax-profiles/{id}/rates', {
          params: { path: { id: profile.id } },
          body: {
            effectiveFrom: form.effectiveFrom,
            components: form.components.map((component) => ({
              code: component.code,
              rateBasisPoints: toBasisPoints(Number(component.percent)),
            })),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['effectiveFrom'],
        codeFields: { 'PHX-CAT-0701': 'effectiveFrom', 'PHX-CAT-0504': 'effectiveFrom' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-202-TAX-RATE"
      titleKey="cat:tax_profiles.add_rate"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('cat:tax_profiles.rate_help', { name: profile.name })}
        </Typography>
        <JalaliDatePickerRHF
          control={control}
          name="effectiveFrom"
          labelKey="cat:prices.effective_from"
          helperKey="cat:prices.effective_from_help"
          required
        />
        {components.fields.map((field, index) => (
          <Stack key={field.id} direction="row" spacing={1} alignItems="flex-start">
            <TextFieldRHF
              control={control}
              name={`components.${index}.code`}
              labelKey="cat:tax_profiles.component_code"
              required
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': `tax-rate-code-${index}` } }}
            />
            <TextFieldRHF
              control={control}
              name={`components.${index}.percent`}
              labelKey="cat:tax_profiles.component_percent"
              type="number"
              required
              slotProps={{
                htmlInput: { min: 0, max: 100, step: 0.01, 'data-testid': `tax-rate-percent-${index}` },
              }}
            />
            <IconButton
              aria-label={t('cat:tax_profiles.remove_component')}
              disabled={components.fields.length <= 1}
              onClick={() => components.remove(index)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
        <Button
          startIcon={<AddIcon />}
          disabled={components.fields.length >= 5}
          onClick={() => components.append({ code: '', percent: '' })}
        >
          {t('cat:tax_profiles.add_component')}
        </Button>
      </Stack>
    </FormDialog>
  );
}

/** تب پروفایل‌های مالیاتی. */
export function TaxProfilesTab() {
  const { t } = useTranslation('cat');
  const write = useWriteAccess('cat.tax.manage');
  const profiles = useTaxProfiles();
  const [editing, setEditing] = useState<TaxProfileView | null | undefined>(undefined);
  const [rating, setRating] = useState<TaxProfileView | null>(null);

  const columns: DataTableColumn<TaxProfileView>[] = [
    { id: 'code', headerKey: 'cat:tax_profiles.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'name',
      headerKey: 'cat:tax_profiles.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{row.name}</span>
          {row.isDefault && (
            <Chip size="small" variant="outlined" label={t('cat:tax_profiles.default_badge')} />
          )}
          {!row.isActive && <Chip size="small" label={t('cat:tax_profiles.inactive_badge')} />}
        </Stack>
      ),
    },
    {
      id: 'category',
      headerKey: 'cat:tax_profiles.category',
      cell: (row) => t(`cat:tax_category.${row.category}`),
    },
    {
      id: 'rates',
      headerKey: 'cat:tax_profiles.rates',
      cell: (row) =>
        row.category === 'Exempt' ? (
          <Typography variant="body2" color="text.secondary">
            {row.exemptionRule}
          </Typography>
        ) : row.rates.filter((rate) => rate.state === 'Current' || rate.state === 'Scheduled').length ===
          0 ? (
          <Typography variant="body2" color="warning.main">
            {t('cat:tax_profiles.no_rate')}
          </Typography>
        ) : (
          <Stack spacing={0.25} data-testid={`tax-rates-${row.code}`}>
            {row.rates
              .filter((rate) => rate.state === 'Current' || rate.state === 'Scheduled')
              .map((rate) => (
                <Typography key={rate.id} variant="body2">
                  {t(
                    rate.state === 'Current'
                      ? 'cat:tax_profiles.rate_current'
                      : 'cat:tax_profiles.rate_scheduled',
                    {
                      percent: faDigits(fromBasisPoints(rate.totalBasisPoints) ?? 0),
                    },
                  )}{' '}
                  <JalaliDate value={rate.effectiveFrom} />
                </Typography>
              ))}
          </Stack>
        ),
    },
    { id: 'services', headerKey: 'cat:tax_profiles.services', cell: (row) => faDigits(row.serviceCount) },
    {
      id: 'actions',
      headerKey: 'cat:prices.columns.actions',
      cell: (row) => (
        <Stack direction="row">
          <IconButton
            aria-label={t('cat:tax_profiles.edit_named', { name: row.name })}
            disabled={!write.canWrite}
            onClick={() => setEditing(row)}
          >
            <EditOutlinedIcon />
          </IconButton>
          {row.category !== 'Exempt' && (
            <Tooltip title={t('cat:tax_profiles.add_rate')}>
              <span>
                <IconButton
                  aria-label={t('cat:tax_profiles.add_rate_named', { name: row.name })}
                  disabled={!write.canWrite}
                  onClick={() => setRating(row)}
                  data-testid={`tax-rate-add-${row.code}`}
                >
                  <PercentIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Typography variant="body2" color="text.secondary">
        {t('cat:tax_profiles.help')}
      </Typography>
      <DataTable
        captionKey="cat:tax_profiles.caption"
        columns={columns}
        rows={profiles.data?.items}
        getRowId={(row) => row.id}
        isLoading={profiles.isPending}
        error={profiles.error}
        onRetry={() => void profiles.refetch()}
        emptyTitleKey="cat:tax_profiles.empty"
        toolbar={
          <Stack direction="row" sx={{ p: 1.5 }}>
            <Button
              startIcon={<AddIcon />}
              disabled={!write.canWrite}
              onClick={() => setEditing(null)}
              data-testid="tax-profile-create"
            >
              {t('cat:tax_profiles.add')}
            </Button>
          </Stack>
        }
      />
      {editing !== undefined && <ProfileDialog profile={editing} onClose={() => setEditing(undefined)} />}
      {rating && <RateDialog profile={rating} onClose={() => setRating(null)} />}
    </Stack>
  );
}
